/**
 * React Query mutation hook for deleting an invoice
 * Handles invoice deletion with automatic query invalidation
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteInvoice } from 'services/billingServices';
import { queryKeys } from '../../../config/queryClient';

/**
 * Hook to delete an invoice using React Query mutation
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onSuccess - Callback function called on successful deletion
 * @param {Function} options.onError - Callback function called on deletion error
 *
 * @returns {Object} Mutation object
 * @returns {Function} returns.mutate - Function to trigger the mutation
 * @returns {Function} returns.mutateAsync - Async function to trigger the mutation
 * @returns {boolean} returns.isLoading - Loading state
 * @returns {boolean} returns.isError - Error state
 * @returns {Object|null} returns.error - Error object if mutation failed
 *
 * @example
 * const deleteInvoiceMutation = useDeleteInvoiceQuery({
 *   onSuccess: () => {
 *     console.log('Invoice deleted successfully');
 *   },
 *   onError: (error) => {
 *     console.error('Failed to delete invoice:', error);
 *   },
 * });
 *
 * // Usage
 * deleteInvoiceMutation.mutate({
 *   invoice_sk: 'INVOICE#123',
 *   is_lead: false,
 * });
 */
export const useDeleteInvoiceQuery = ({ onSuccess, onError } = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ invoice_sk, is_lead = false }) => {
      return deleteInvoice(invoice_sk, is_lead);
    },
    onSuccess: (data, variables, context) => {
      // Invalidate all invoice queries to refresh the list
      queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });

      // Call custom onSuccess callback if provided
      if (onSuccess) {
        onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Call custom onError callback if provided
      if (onError) {
        onError(error, variables, context);
      }
    },
  });
};

export default useDeleteInvoiceQuery;
